import { useEffect, useRef, useState } from 'react';
import { Category } from '../api/types';
import { theme } from '../theme';
import { Icon } from '../components/Icon';
import { fmtPlanNum } from '../utils/plan-pad';
import { swapAdjacent, renumberChanged } from '../utils/category-order';

interface Props {
  /** Начальный упорядоченный список всех категорий из viewData. */
  initialCats: Category[];
  updateCategory: (input: { name: string; sort_order: number }) => Promise<unknown>;
}

export function OrderTab({ initialCats, updateCategory }: Props) {
  const { sub, ink } = theme.colors;

  // Разбиваем при монтировании — один раз; свежий viewData не перетирает.
  const [expense, setExpense] = useState<Category[]>(() =>
    initialCats
      .filter((c) => c.type === 'Расход')
      .slice()
      .sort((a, b) => a.sort_order - b.sort_order),
  );
  const [income, setIncome] = useState<Category[]>(() =>
    initialCats
      .filter((c) => c.type === 'Доход')
      .slice()
      .sort((a, b) => a.sort_order - b.sort_order),
  );

  // Dirty-флаг: true когда пользователь сделал хотя бы один свап.
  const dirtyRef = useRef(false);

  // Ref-ы на актуальные значения — избегаем stale-closure в cleanup.
  const expenseRef = useRef(expense);
  const incomeRef = useRef(income);
  const updateCategoryRef = useRef(updateCategory);

  useEffect(() => { expenseRef.current = expense; }, [expense]);
  useEffect(() => { incomeRef.current = income; }, [income]);
  useEffect(() => { updateCategoryRef.current = updateCategory; }, [updateCategory]);

  // Батч-сохранение при размонтировании.
  useEffect(() => {
    return () => {
      if (!dirtyRef.current) return;
      const changes = [
        ...renumberChanged(expenseRef.current),
        ...renumberChanged(incomeRef.current),
      ];
      for (const { name, sort_order } of changes) {
        updateCategoryRef.current({ name, sort_order }).catch(() => {
          // fire-and-forget; тосты не показываем
        });
      }
    };
  }, []);

  function swap(
    group: 'expense' | 'income',
    index: number,
    dir: -1 | 1,
  ) {
    dirtyRef.current = true;
    if (group === 'expense') {
      setExpense((prev) => swapAdjacent(prev, index, dir));
    } else {
      setIncome((prev) => swapAdjacent(prev, index, dir));
    }
  }

  return (
    <div>
      {/* Курсив-пояснение */}
      <p
        style={{
          fontSize: 13,
          color: sub,
          fontStyle: 'italic',
          margin: '14px 0 10px',
        }}
      >
        порядок здесь — это порядок чипов в AddSheet и списках; номера пишутся сами
      </p>

      {/* Расходные */}
      {expense.map((c, i) => (
        <OrderRow
          key={c.name}
          cat={c}
          index={i}
          total={expense.length}
          group="expense"
          onSwap={swap}
        />
      ))}

      {/* Заголовок доходных — только если есть хоть одна доходная категория */}
      {income.length > 0 && (
        <div
          style={{
            textTransform: 'uppercase',
            letterSpacing: '.14em',
            fontSize: 11,
            color: sub,
            borderBottom: `1px solid ${ink}`,
            padding: '16px 0 8px',
          }}
        >
          Доходные
        </div>
      )}

      {/* Доходные */}
      {income.map((c, i) => (
        <OrderRow
          key={c.name}
          cat={c}
          index={i}
          total={income.length}
          group="income"
          onSwap={swap}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Строка
// ---------------------------------------------------------------------------

function OrderRow({
  cat,
  index,
  total,
  group,
  onSwap,
}: {
  cat: Category;
  index: number;
  total: number;
  group: 'expense' | 'income';
  onSwap: (group: 'expense' | 'income', index: number, dir: -1 | 1) => void;
}) {
  const { sub, line, subSoft } = theme.colors;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '11px 0',
        borderBottom: `1px solid ${line}`,
      }}
    >
      {/* Grip */}
      <span style={{ flexShrink: 0 }}>
        <Icon name="grip" size={14} color={subSoft} stroke={1.6} />
      </span>

      {/* Имя */}
      <span style={{ flex: 1, minWidth: 0, fontSize: 15 }}>{cat.name}</span>

      {/* План */}
      <span
        style={{
          fontSize: 12,
          color: sub,
          fontVariantNumeric: 'tabular-nums',
          flexShrink: 0,
        }}
      >
        {fmtPlanNum(cat.plan_month)}
      </span>

      {/* Кнопка ↑ */}
      <button
        type="button"
        disabled={index === 0}
        onClick={() => onSwap(group, index, -1)}
        style={{
          width: 34,
          height: 30,
          background: 'transparent',
          border: `1px solid ${line}`,
          borderRadius: theme.radius.sm,
          color: sub,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          cursor: index === 0 ? 'default' : 'pointer',
          opacity: index === 0 ? 0.35 : 1,
          padding: 0,
        }}
        aria-label="переместить вверх"
      >
        <Icon name="chevronUp" size={14} stroke={1.4} />
      </button>

      {/* Кнопка ↓ */}
      <button
        type="button"
        disabled={index === total - 1}
        onClick={() => onSwap(group, index, 1)}
        style={{
          width: 34,
          height: 30,
          background: 'transparent',
          border: `1px solid ${line}`,
          borderRadius: theme.radius.sm,
          color: sub,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          cursor: index === total - 1 ? 'default' : 'pointer',
          opacity: index === total - 1 ? 0.35 : 1,
          padding: 0,
        }}
        aria-label="переместить вниз"
      >
        <Icon name="chevronDown" size={14} stroke={1.4} />
      </button>
    </div>
  );
}
